import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Badge, Button, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Select from 'react-select';
import { useDebounce } from 'use-debounce';

import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import GDSNProcessManufacturerModal from 'components/data_sources/single_data_source_view/GDSNProcessManufacturerModal';
import { GLOBAL_MANUFACTURERS } from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import ManufacturerGLNStatus, {
  DefaultSelectedGLNStatuses,
  ManufacturerGLNStatusOptions,
} from 'lib/enums/ManufacturerGLNStatus';
import PaginationComponent from 'components/shared/PaginationComponent';
import useGet from 'lib/hooks/useGet';

function DataSourceSubscribedManufacturersTab({
  dataSource,
  processDataSource,
  processing,
  refetchRun,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [glnSearchTerm, setGlnSearchTerm] = useState('');
  const [selectedGlnStatuses, setSelectedGlnStatuses] = useState(DefaultSelectedGLNStatuses);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [debouncedGlnSearchTerm] = useDebounce(glnSearchTerm, 500);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingManufacturer, setProcessingManufacturer] = useState(null);
  const offset = (currentPage - 1) * itemsPerPage;

  // Convert selected statuses to array of values for API call
  const glnStatusValues = selectedGlnStatuses.map(status => status.value);

  const {
    data: manufacturersData = { manufacturers: [], count: 0 },
    loading,
    error,
    refetch,
  } = useGet(
    GLOBAL_MANUFACTURERS,
    {
      limit: itemsPerPage,
      offset,
      query: debouncedSearchTerm,
      gln_search: debouncedGlnSearchTerm,
      gln_statuses: glnStatusValues.length > 0 ? glnStatusValues.join(',') : '',
      only_with_glns: true,
    },
    false,
  );

  const { manufacturers, count } = manufacturersData;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, glnSearchTerm, selectedGlnStatuses, itemsPerPage]);

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleGlnSearch = e => {
    setGlnSearchTerm(e.target.value);
  };

  const handleStatusChange = selectedOptions => {
    setSelectedGlnStatuses(selectedOptions || []);
  };

  const handleProcessClick = (e, manufacturer) => {
    e.stopPropagation();
    setProcessingManufacturer(manufacturer);
    setShowProcessModal(true);
  };

  function handleProcessNow(manufacturerId, manufacturerGlns, lookbackDays) {
    processDataSource({
      data_source_id: dataSource.id,
      manufacturer_id: manufacturerId,
      manufacturer_glns: manufacturerGlns,
      lookback_days: lookbackDays,
      process_delta_file: false,
    }).then(() => {
      setTimeout(() => {
        refetchRun();
      }, 1000);
    });
    setShowProcessModal(false);
    setProcessingManufacturer(null);
  }

  const renderGLNStatus = glnStatus => {
    if (!glnStatus || Object.keys(glnStatus).length === 0) {
      return <Badge variant="secondary">No GLN Status</Badge>;
    }

    const getStatusVariant = status => {
      switch (status) {
        case ManufacturerGLNStatus.SUCCESS:
          return 'success';
        case ManufacturerGLNStatus.SUBSCRIBED:
          return 'primary';
        case ManufacturerGLNStatus.SUBMITTED:
          return 'info';
        case ManufacturerGLNStatus.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    };

    return (
      <div className={css(styles.glnList)}>
        {Object.entries(glnStatus).map(([gln, status]) => (
          <div key={gln} className={css(styles.glnItem)}>
            <strong>{gln}</strong>
            <Badge variant={getStatusVariant(status)} className="ml-2" size="sm">
              {status}
            </Badge>
          </div>
        ))}
      </div>
    );
  };

  const renderGLNs = glns => {
    if (!glns || Object.keys(glns).length === 0) {
      return <span className="text-muted">No GLNs</span>;
    }

    return (
      <div className={css(styles.glnList)}>
        {Object.entries(glns).map(([gln, companyName]) => (
          <div key={gln} className={css(styles.glnItem)}>
            <strong>{gln}</strong>
            {companyName && <span className="text-muted ml-2">({companyName})</span>}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="alert alert-danger">Error loading manufacturers</div>;
  }

  return (
    <div className="mt-4">
      <GDSNProcessManufacturerModal
        show={showProcessModal}
        onHide={() => {
          setShowProcessModal(false);
          setProcessingManufacturer(null);
        }}
        manufacturer={processingManufacturer}
        dataSource={dataSource}
        onSubmit={handleProcessNow}
      />
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Search Manufacturers</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by manufacturer name..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Search by GLN</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by GLN..."
              value={glnSearchTerm}
              onChange={handleGlnSearch}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>GLN Status Filter</Form.Label>
            <Select
              isMulti
              value={selectedGlnStatuses}
              onChange={handleStatusChange}
              options={ManufacturerGLNStatusOptions}
              placeholder="Select GLN statuses..."
              isClearable
              className={css(styles.glnStatusFilter)}
            />
            <Form.Text className="text-muted">
              Multiple selections allowed. Default: Success & Subscribed
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="d-flex justify-content-between mb-3">
        <Col>
          <strong>Total Manufacturers: {count}</strong>
          {selectedGlnStatuses.length > 0 && (
            <span className="ml-3 text-muted">
              (Filtered by: {selectedGlnStatuses.map(s => s.label).join(', ')})
            </span>
          )}
        </Col>
        {count > 0 && (
          <Col className="d-flex justify-content-end">
            <PaginationComponent
              totalItems={count}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </Col>
        )}
      </Row>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Manufacturer Name</th>
            <th>CND ID</th>
            <th>GLNs</th>
            <th>GLN Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {manufacturers.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center">
                No manufacturers found
              </td>
            </tr>
          ) : (
            manufacturers.map(manufacturer => (
              <tr key={manufacturer.id}>
                <td>{manufacturer.id}</td>
                <td>{manufacturer.name}</td>
                <td>{manufacturer.cnd_id || 'N/A'}</td>
                <td>{renderGLNs(manufacturer.manufacturer_glns)}</td>
                <td>{renderGLNStatus(manufacturer.manufacturer_glns_status)}</td>
                <td>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={e => handleProcessClick(e, manufacturer)}
                    disabled={processing}
                  >
                    Process Manufacturer
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
}

const styles = StyleSheet.create({
  glnList: {
    maxHeight: '100px',
    overflowY: 'auto',
  },
  glnItem: {
    marginBottom: '4px',
    fontSize: '12px',
  },
  glnStatusFilter: {
    zIndex: 100,
  },
});

DataSourceSubscribedManufacturersTab.propTypes = {
  dataSource: PropTypes.object.isRequired,
  processDataSource: PropTypes.func,
  processing: PropTypes.bool,
  refetchRun: PropTypes.func,
};

export default DataSourceSubscribedManufacturersTab;
